import React, { useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import moment from "moment";
import {
  editPrinter,
  setPrinterRunning,
  disablePrinter,
} from "../../actions/printer";
import { heartbeatMinutes, lastSeenText } from "../../utils/heartbeat";
import { HEALTH, healthOf } from "../../utils/printerHealth";
import { canAccess } from "../../utils/access";
import { rearTrayText, photosOff } from "../../utils/rearTray";
import { placeOf, isListed, pinText, mapUrl } from "../../utils/place";
import {
  TABS,
  TANKS,
  INK_FIELDS,
  savedOf,
  changedIn,
  problemsOf,
  changesOf,
  PrinterTabs,
  PrinterFields,
} from "./PrinterForm";

const actionClass =
  "px-4 py-2 text-sm font-medium leading-5 rounded-lg focus:outline-none";

const secondaryButton =
  "flex-1 px-5 py-3 text-sm font-medium leading-5 text-gray-700 transition-colors duration-150 border border-gray-300 rounded-lg dark:text-gray-400 sm:flex-none sm:px-4 sm:py-2 active:bg-transparent hover:border-gray-500 focus:border-gray-500 active:text-gray-500 focus:outline-none focus:shadow-outline-gray";

const primaryButton =
  "flex-1 px-5 py-3 text-sm font-medium leading-5 text-white transition-colors duration-150 bg-purple-600 border border-transparent rounded-lg sm:flex-none sm:px-4 sm:py-2 active:bg-purple-600 hover:bg-purple-700 focus:outline-none focus:shadow-outline-purple dark:text-gray-300";

const linkClass = "text-purple-600 underline dark:text-purple-400";

// The details have a tab for each tab of the form, and one for what the kiosk reports
// of itself, which nobody edits
const KIOSK_TAB = "kiosk";
const DETAIL_TABS = [
  ...TABS,
  { key: KIOSK_TAB, label: "Kiosk", icon: "fa-desktop", fields: [] },
];

// Every tab is as tall as the tallest of its kind, the details or the form, so the
// sheet keeps its size from tab to tab. A screen too short for it shrinks the tab,
// which then scrolls.
const DETAILS_HEIGHT = "23rem";
const FORM_HEIGHT = "27rem";

const orNA = (value) =>
  value === undefined || value === null || value === "" ? "NA" : value;

const detailRow = (label, value) => (
  <div className="flex justify-between py-2">
    <span className="text-gray-500 dark:text-gray-400">{label}</span>
    <span className="ml-4 text-right text-gray-700 dark:text-gray-300">
      {value}
    </span>
  </div>
);

// The field can hold several numbers, like "88987266 90956161". One tel: link each.
const phoneLinks = (phoneNumber) => (
  <span className="flex flex-wrap justify-end">
    {String(phoneNumber)
      .split(/[\s,;/]+/)
      .filter(Boolean)
      .map((number, i) => (
        <a key={i} href={"tel:" + number} className={"ml-3 " + linkClass}>
          {number}
        </a>
      ))}
  </span>
);

// An ink tank as the backend estimates it: its marks, and what they print at the
// kiosk's pace
const inkText = (tank) => {
  if (!tank) return "Not recorded yet";
  return (
    tank.level +
    " of " +
    tank.marks +
    " marks · about " +
    tank.pagesLeft +
    " pages" +
    (typeof tank.daysLeft === "number"
      ? " · " + Math.floor(tank.daysLeft) + " days"
      : "")
  );
};

// When a tank's marks were last recorded, of either tank
const inkDate = (ink) => {
  const seen = TANKS.map((name) => ink && ink[name])
    .filter(Boolean)
    .map((tank) => tank.seenAt)
    .sort();
  return seen.length > 0
    ? moment(seen[seen.length - 1]).format("YYYY-MM-DD")
    : "NA";
};

// Whether the kiosks list the printer in their "Байршил" window, and why not when it
// is switched on
const listedText = (printer) => {
  if (isListed(printer)) return "Listed";
  return placeOf(printer).listed === true
    ? "Not listed: it has no location"
    : "Not listed";
};

// What the kiosk reported in its last heartbeat. The kiosk decides what it sends,
// so any field can be missing.
const KioskDetails = ({ printer }) => {
  const telemetry = printer.telemetry;
  if (!telemetry) {
    // The API leaves out an empty telemetry object, so a heartbeat without a body
    // gets here with lastSeenAt set.
    return (
      <p className="py-2 text-gray-500 dark:text-gray-400">
        {printer.lastSeenAt
          ? "The last heartbeat had no kiosk details."
          : "This kiosk has not sent a heartbeat yet."}
      </p>
    );
  }

  const app = telemetry.app || {};
  const update = telemetry.lastUpdate;
  const kioskPrinter = telemetry.printer || {};
  const system = telemetry.system || {};
  const gigabytes = (mb) =>
    typeof mb === "number" ? (mb / 1024).toFixed(1) + " GB" : "NA";

  let updateText = "NA";
  if (update && update.error) {
    updateText = update.error;
  } else if (update) {
    updateText =
      orNA([update.outcome, update.version].filter(Boolean).join(" ")) +
      (update.ranAt ? " · " + String(update.ranAt).replace("T", " ") : "") +
      // Why the updater did what it did, e.g. why a release was rolled back.
      (update.detail ? " · " + update.detail : "");
  }

  // printerErrors is missing while the kiosk is offline, and then only the number is left.
  const problems = printer.printerErrors;
  const printerParts = [kioskPrinter.name];
  if (Array.isArray(problems)) {
    printerParts.push(problems.length > 0 ? problems.join(" ") : "Ready");
  } else if (kioskPrinter.statusNumber !== undefined) {
    printerParts.push("status " + kioskPrinter.statusNumber);
  }
  if (kioskPrinter.jobs !== undefined) {
    printerParts.push(kioskPrinter.jobs + " jobs");
  }

  return (
    <>
      {detailRow(
        "Version",
        orNA(app.version) + (app.node ? " · Node " + app.node : "")
      )}
      {detailRow(
        "Backend running for",
        typeof app.uptimeSec === "number"
          ? moment.duration(app.uptimeSec, "seconds").humanize()
          : "NA"
      )}
      {detailRow("Last update", updateText)}
      {detailRow(
        "Printer",
        <span
          className={
            problems && problems.length > 0
              ? "text-red-600 dark:text-red-400"
              : ""
          }
        >
          {kioskPrinter.error ||
            orNA(
              printerParts.filter((part) => part !== undefined).join(" · ")
            )}
        </span>
      )}
      {detailRow("Disk free", gigabytes(system.diskFreeMb))}
      {detailRow(
        "Memory free",
        gigabytes(system.memFreeMb) + " / " + gigabytes(system.memTotalMb)
      )}
    </>
  );
};

// Over the tabs of the details, whichever is open: the printer's error, to resolve,
// or that it is disabled, to enable it again
const StatusBanner = ({ printer, setPrinterRunning }) => {
  if (printer.status === "error") {
    return (
      <div className="flex-shrink-0 p-3 mx-6 mt-3 bg-red-100 rounded-md dark:bg-red-900">
        <h2 className="text-sm font-semibold text-red-700 dark:text-red-100">
          Unresolved error
        </h2>
        <p className="mt-1 overflow-y-auto text-sm text-gray-700 max-h-24 dark:text-gray-300">
          {printer.error}
        </p>
        <button
          className={
            actionClass + " mt-3 text-white bg-purple-600 hover:bg-purple-700"
          }
          onClick={() =>
            setPrinterRunning(printer._id, "Error marked as resolved")
          }
        >
          Mark as resolved
        </button>
      </div>
    );
  }

  if (printer.status === "disabled") {
    return (
      <div className="flex items-center justify-between flex-shrink-0 p-3 mx-6 mt-3 bg-gray-100 rounded-md dark:bg-gray-700">
        <p className="text-sm text-gray-700 dark:text-gray-300">
          This printer is disabled.
        </p>
        <button
          className={
            actionClass + " ml-2 text-white bg-purple-600 hover:bg-purple-700"
          }
          onClick={() =>
            setPrinterRunning(printer._id, "Printer has been enabled")
          }
        >
          Enable
        </button>
      </div>
    );
  }

  return null;
};

// The details of the open tab. The General tab is where a running printer is taken
// out of service, when the role may.
const PrinterDetails = ({ tab, printer, ink, canDisable, disablePrinter }) => {
  if (tab === "paper") {
    return (
      <>
        {detailRow("A4 paper", printer.paper ? printer.paper : 0)}
        {detailRow(
          "Paper date",
          printer.paperDate ? printer.paperDate.split("T")[0] : "NA"
        )}
        {detailRow("Black ink", inkText(ink && ink.black))}
        {detailRow("Color ink", inkText(ink && ink.color))}
        {detailRow("Ink date", inkDate(ink))}
        {detailRow(
          "Total printed",
          printer.totalPrinted ? printer.totalPrinted : 0
        )}
        {detailRow("Photo printing", photosOff(printer) ? "Off" : "On")}
        {detailRow("Rear tray", rearTrayText(printer))}
        {printer.rearTray &&
          printer.rearTray.paperDate &&
          detailRow("Rear tray date", printer.rearTray.paperDate.split("T")[0])}
      </>
    );
  }

  if (tab === "location") {
    const place = placeOf(printer);
    return (
      <>
        {detailRow("Address", orNA(place.address))}
        {detailRow("Opening hours", orNA(place.hours))}
        {detailRow(
          "Coordinates",
          pinText(printer) ? (
            <a
              href={mapUrl(place)}
              target="_blank"
              rel="noopener noreferrer"
              className={linkClass}
            >
              {pinText(printer)}
            </a>
          ) : (
            "NA"
          )
        )}
        {detailRow("Kiosks' Locations window", listedText(printer))}
      </>
    );
  }

  if (tab === KIOSK_TAB) return <KioskDetails printer={printer} />;

  return (
    <>
      {detailRow(
        "Phone number",
        printer.phoneNumber ? phoneLinks(printer.phoneNumber) : "NA"
      )}
      {detailRow("Heartbeat", "every " + heartbeatMinutes(printer) + " min")}
      {canDisable &&
        printer.status !== "error" &&
        printer.status !== "disabled" && (
          <div className="pt-4">
            <button
              className={
                actionClass +
                " text-red-600 border border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-700 dark:hover:bg-gray-700"
              }
              onClick={() => {
                if (window.confirm("Disable " + printer.printerName + "?")) {
                  disablePrinter(printer._id);
                }
              }}
            >
              Disable printer
            </button>
          </div>
        )}
    </>
  );
};

// Opens on the printer's details, a tab at a time. Edit swaps the open tab for its
// part of the form. The header and footer stay put and only the tab scrolls, so Save
// is always on screen.
// ink: the kiosk's tanks from GET /api/printer/ink, so the sheet shows the state its
// card does, and marks: the lines of a tank. onInkSaved loads them again once a save
// has recorded ink.
const EditPrinter = ({
  auth: { user },
  printer,
  ink,
  marks,
  onInkSaved,
  setShowModal,
  editPrinter,
  setPrinterRunning,
  disablePrinter,
}) => {
  // The open tab, of the details or, while editing, of the form
  const [tab, setTab] = useState(TABS[0].key);
  const [editing, setEditing] = useState(false);
  // What was typed over the printer's own values (PrinterForm.js)
  const [typed, setTyped] = useState({});
  const [saving, setSaving] = useState(false);

  const saved = savedOf(printer);
  const form = { ...saved, ...typed };
  const changed = changedIn(form, saved);
  const problems = problemsOf(form, changed);
  const problemField = Object.keys(problems)[0];
  const canSave = changed.length > 0 && !problemField && !saving;

  const setField = (key, value) =>
    setTyped((current) => ({ ...current, [key]: value }));
  const stopEditing = () => {
    setTyped({});
    setEditing(false);
  };
  // Leaving the form loses what was typed into it
  const mayLeave = () =>
    changed.length === 0 ||
    window.confirm("Discard the changes you have not saved?");
  const cancel = () => {
    if (mayLeave()) stopEditing();
  };
  const close = () => {
    if (mayLeave()) setShowModal(false);
  };
  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    const done = await editPrinter(printer._id, changesOf(form, changed));
    setSaving(false);
    if (!done) return;
    stopEditing();
    if (changed.some((key) => INK_FIELDS.includes(key))) onInkSaved();
  };

  const health = HEALTH[healthOf(printer, ink)];
  return (
    <>
      {/* Only a press on the backdrop itself closes the sheet */}
      <div
        className="fixed inset-0 z-30 flex items-end pt-12 bg-black bg-opacity-50 sm:items-center sm:justify-center sm:p-4"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) {
            close();
          }
        }}
      >
        <div className="flex flex-col w-full max-h-full overflow-hidden bg-white rounded-t-lg dark:bg-gray-800 sm:rounded-lg sm:max-w-xl">
          <header className="flex items-start justify-between flex-shrink-0 px-6 pt-5 pb-1">
            <div className="min-w-0">
              <p className="text-lg font-semibold text-gray-700 truncate dark:text-gray-300">
                {editing ? "Edit " : ""}
                {printer.printerName}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {printer.location ? printer.location : "No location"}
                {" · "}
                {printer.lastSeenAt
                  ? "last seen " + lastSeenText(printer)
                  : "no heartbeat yet"}
              </p>
            </div>
            <div className="flex items-center flex-shrink-0 ml-2">
              <span
                className={
                  "px-2 py-1 text-xs font-semibold leading-tight rounded-full " +
                  health.badge
                }
              >
                {health.label}
              </span>
              <button
                onClick={close}
                className="inline-flex items-center justify-center w-6 h-6 ml-3 text-gray-400 transition-colors duration-150 rounded dark:hover:text-gray-200 hover:text-gray-700"
                aria-label="close"
              >
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  role="img"
                  aria-hidden="true"
                >
                  <path
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                    fillRule="evenodd"
                  ></path>
                </svg>
              </button>
            </div>
          </header>

          {!editing && (
            <StatusBanner
              printer={printer}
              setPrinterRunning={setPrinterRunning}
            />
          )}
          <PrinterTabs
            tabs={editing ? TABS : DETAIL_TABS}
            tab={tab}
            onTab={setTab}
            changed={changed}
            problems={problems}
          />
          {editing ? (
            <form
              id="printer-form"
              role="tabpanel"
              className="min-h-0 px-6 py-5 space-y-5 overflow-y-auto"
              style={{ height: FORM_HEIGHT }}
              onSubmit={(e) => {
                e.preventDefault();
                save();
              }}
            >
              <PrinterFields
                tab={tab}
                form={form}
                problems={problems}
                onChange={setField}
                ink={ink}
                marks={marks}
              />
            </form>
          ) : (
            <div
              role="tabpanel"
              className="min-h-0 px-6 py-3 overflow-y-auto text-sm divide-y divide-gray-100 dark:divide-gray-700"
              style={{ height: DETAILS_HEIGHT }}
            >
              <PrinterDetails
                tab={tab}
                printer={printer}
                ink={ink}
                canDisable={canAccess(user, "printer.disable")}
                disablePrinter={disablePrinter}
              />
            </div>
          )}

          <footer className="flex items-center justify-end flex-shrink-0 px-6 py-3 space-x-3 border-t border-gray-100 bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
            {editing ? (
              <>
                {/* Why Save is off, also when the field is on another tab */}
                {problemField && (
                  <p className="hidden min-w-0 mr-auto text-xs text-red-600 truncate sm:block dark:text-red-400">
                    {
                      TABS.find(({ fields }) => fields.includes(problemField))
                        .label
                    }
                    : {problems[problemField]}
                  </p>
                )}
                <button
                  type="button"
                  onClick={cancel}
                  className={secondaryButton}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="printer-form"
                  disabled={!canSave}
                  className={
                    primaryButton +
                    (canSave ? "" : " opacity-50 cursor-not-allowed")
                  }
                >
                  {saving && (
                    <i
                      className="mr-2 fas fa-spinner fa-spin"
                      aria-hidden="true"
                    />
                  )}
                  Save
                </button>
              </>
            ) : (
              <>
                <button onClick={close} className={secondaryButton}>
                  Close
                </button>
                {tab !== KIOSK_TAB && (
                  <button
                    onClick={() => setEditing(true)}
                    className={primaryButton}
                  >
                    Edit
                  </button>
                )}
              </>
            )}
          </footer>
        </div>
      </div>
    </>
  );
};

EditPrinter.propTypes = {
  auth: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, {
  editPrinter,
  setPrinterRunning,
  disablePrinter,
})(EditPrinter);
